import { defineStore } from 'pinia';
import { ref } from 'vue';
import { resolveTheme, THEME_STORAGE_KEY, type Theme } from '@/utils/theme';

function readStoredTheme(): string | null {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

export const useThemeStore = defineStore('theme', () => {
  const theme = ref<Theme>(resolveTheme(readStoredTheme(), prefersDark()));
  applyTheme(theme.value);

  // Until the user picks one, keep tracking the operating system setting.
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (event) => {
    if (readStoredTheme() !== null) return;
    theme.value = resolveTheme(null, event.matches);
    applyTheme(theme.value);
  });

  function setTheme(next: Theme): void {
    theme.value = next;
    applyTheme(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Private browsing or blocked storage: the choice lasts for this page load only.
    }
  }

  function toggleTheme(): void {
    setTheme(theme.value === 'dark' ? 'light' : 'dark');
  }

  return { theme, setTheme, toggleTheme };
});
